import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../auth/api";
import { useAuth } from "../auth/AuthContext";

/* ──────────────────────────────────────────────────────────────
   Utils CSV
   ────────────────────────────────────────────────────────────── */
function toCSV(rows, columns) {
    const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = columns.map(c => esc(c.label)).join(",");
    const body = rows.map(r => columns.map(c => esc(c.accessor(r))).join(",")).join("\n");
    return head + "\n" + body;
}
function download(filename, text) {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/* ──────────────────────────────────────────────────────────────
   Combo de Lotes (asíncrono + cursor + virtualizado)  ⬇️
   ────────────────────────────────────────────────────────────── */
function Debounced(value, delay = 250) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

// Virtualización mínima sin libs: solo renderiza visible+buffer
function VirtualList({ items, rowHeight = 40, height = 320, renderRow }) {
    const [scrollTop, setScrollTop] = useState(0);
    const onScroll = (e) => setScrollTop(e.currentTarget.scrollTop);
    const total = items.length;
    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - 5);
    const endIndex = Math.min(total, Math.ceil((scrollTop + height) / rowHeight) + 5);
    const paddingTop = startIndex * rowHeight;
    const visible = items.slice(startIndex, endIndex);

    return (
        <div style={{ height, overflow: "auto" }} onScroll={onScroll}>
            <div style={{ height: total * rowHeight, position: "relative" }}>
                <div style={{ position: "absolute", top: paddingTop, left: 0, right: 0 }}>
                    {visible.map((item, i) => (
                        <div key={item.id} style={{ height: rowHeight }}>
                            {renderRow(item, startIndex + i)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function BatchCombo({
    accountId,
    value,
    onChange,
    mine = false,
    defaultStatus = "",
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const dq = Debounced(q, 250);
    const [status, setStatus] = useState(defaultStatus);
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);

    // para enfoque inicial
    const inputRef = useRef(null);

    // bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // cerrar con ESC
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // cargar página
    const noCache = { headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" } };
    const bust = () => `?_=${Date.now()}`;
    const tryGet = async (path) => { try { return await api.get(`${path}${bust()}`, noCache); } catch { return null; } };

    const fetchPage = async ({ reset = false, cursor = null } = {}) => {
        if (!accountId) return;
        setLoading(true);

        const p = new URLSearchParams();
        if (mine) p.set("mine", "1");
        if (dq) p.set("q", dq);
        if (status) p.set("status", status);
        p.set("limit", "40");
        if (cursor) p.set("cursor", String(cursor));

        const r = await tryGet(`/wa/accounts/${accountId}/batches?` + p.toString());
        const data = r?.data || {};

        const pageItems = Array.isArray(data.items) ? data.items
            : Array.isArray(data.rows) ? data.rows
                : [];
        setItems(prev => reset ? pageItems : [...prev, ...pageItems]);
        setNextCursor(data.nextCursor || null);
        setLoading(false);
    };

    // Primera apertura
    useEffect(() => { if (open) fetchPage({ reset: true }); }, [open]); // eslint-disable-line
    // Cambios de query/filtros
    useEffect(() => { if (open) fetchPage({ reset: true }); }, [dq, status, mine, accountId]); // eslint-disable-line
    // Enfoque en input al abrir
    useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [open]);

    const handleNearEnd = (index) => {
        if (index >= items.length - 5 && nextCursor && !loading) {
            fetchPage({ reset: false, cursor: nextCursor });
        }
    };

    const selectedItem = items.find(x => String(x.id) === String(value)) || null;

    return (
        <div style={{ width: "100%" }}>
            <label style={{ display: "block", marginBottom: 6 }}>Lote</label>
            <div className="input" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                    type="button"
                    className="btn btn-muted"
                    onClick={() => setOpen(true)}
                    title="Seleccionar lote"
                >
                    {value ? `#${value}` : "— Seleccione Uno —"}
                </button>

                <select className="input" value={status} onChange={e => setStatus(e.target.value)} style={{ maxWidth: 160 }}>
                    <option value="">Todos</option>
                    <option value="running">En curso</option>
                    <option value="pending">Pendiente</option>
                    <option value="finished">Finalizado</option>
                    <option value="stopped">Detenido</option>
                    <option value="aborted">Abortado</option>
                </select>

                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={mine} readOnly />
                    Mis lotes
                </label>
            </div>

            {/* Modal */}
            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,.5)", // backdrop semitransparente
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <div
                        className="card section"
                        onClick={(e) => e.stopPropagation()}   // <- evita cierre por click dentro
                        style={{
                            width: "100%",
                            maxWidth: 720,
                            maxHeight: "80vh",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,

                            // 👇 fuerza fondo opaco (ajusta a tu tema)
                            background: "var(--panel-bg, #0f172a)",
                            border: "1px solid var(--border, #1f2937)",
                            boxShadow: "0 10px 30px rgba(0,0,0,.35)",
                            borderRadius: 12
                        }}
                    >
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                                ref={inputRef}
                                className="input"
                                placeholder="Buscar por #id o estado…"
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <button className="btn btn-muted" onClick={() => setOpen(false)}>Cerrar</button>
                        </div>

                        <div style={{ border: "1px solid var(--border)", borderRadius: 8 }}>
                            <VirtualList
                                items={items}
                                height={420}
                                rowHeight={44}
                                renderRow={(b, index) => {
                                    handleNearEnd(index);
                                    const selected = String(value || "") === String(b.id);
                                    return (
                                        <div
                                            onClick={() => { onChange?.(String(b.id)); setOpen(false); }} // <- cierra al elegir
                                            style={{
                                                display: "flex", alignItems: "center", gap: 8,
                                                padding: "0 12px", cursor: "pointer", height: 44,
                                                background: selected ? "rgba(59,130,246,.15)" : "transparent"
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, width: 80 }}>#{b.id}</div>
                                            <div style={{ opacity: .85, width: 120 }}>{b.status}</div>
                                            <div style={{ opacity: .7 }}>tot {b.total ?? 0}</div>
                                            <div style={{ marginLeft: "auto", fontSize: 12, opacity: .6 }}>
                                                {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div className="muted">
                                {loading ? "Cargando..." : nextCursor ? "Desplázate para cargar más" : `${items.length} resultados`}
                                {selectedItem && (
                                    <> · Seleccionado: <b>#{selectedItem.id}</b> ({selectedItem.status})</>
                                )}
                            </div>
                            <button
                                className="btn"
                                onClick={() => fetchPage({ reset: true })}
                                disabled={loading}
                                title="Actualizar"
                            >
                                {loading ? "..." : "Actualizar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────
   Combo de Lotes  ⬆️
   ────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────
   Pantalla principal
   ────────────────────────────────────────────────────────────── */
export default function HistoricoEnvios() {
    const { user } = useAuth();

    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

    // Filtros
    const [accounts, setAccounts] = useState([]);
    const [selectedAccId, setSelectedAccId] = useState("");
    const [batchId, setBatchId] = useState("");       // ahora se selecciona vía BatchCombo
    const [mine, /* setMine */] = useState(false);    // si quieres permitir toggle, pásalo al BatchCombo
    const [userFilter, setUserFilter] = useState("");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");

    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);

    // Datos tabla
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({ total: 0, sent: 0, pending: 0, error: 0 });
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(200);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    // Detalle
    const [preview, setPreview] = useState(null);

    // Validaciones
    const [dateError, setDateError] = useState("");
    const [batchError, setBatchError] = useState("");
    const disableSearch = !!dateError || !!batchError || loading;

    // Endpoints usados por esta vista
    const ep = useMemo(() => {
        const noCache = { headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" } };
        const bust = () => `?_=${Date.now()}`;
        const tryGet = async (path) => { try { return await api.get(`${path}${bust()}`, noCache); } catch { return null; } };

        return {
            listAccounts: async () => {
                const r = await tryGet(`/wa/accounts`);
                return r?.data?.rows || [];
            },
            listBatchJobs: async (accId, batchId, { status = "", q = "", limit = 500, offset = 0, dateFrom = "", dateTo = "", userId = "" } = {}) => {
                const qs = new URLSearchParams();
                if (status) qs.set("status", status);
                if (q) qs.set("q", q);
                if (limit) qs.set("limit", String(limit));
                if (offset) qs.set("offset", String(offset));
                if (dateFrom) qs.set("date_from", dateFrom);
                if (dateTo) qs.set("date_to", dateTo);
                if (userId) qs.set("sent_by", userId);
                const url = `/wa/accounts/${accId}/batches/${batchId}/jobs?` + qs.toString();
                const r = await tryGet(url);
                return r?.data || { rows: [], counts: { total: 0, sent: 0, pending: 0, error: 0 } };
            },
            listByUserAndDate: async (accId, { dateFrom = "", dateTo = "", userId = "", status = "", q = "", limit = 500, offset = 0 } = {}) => {
                const qs = new URLSearchParams();
                if (dateFrom) qs.set("date_from", dateFrom);
                if (dateTo) qs.set("date_to", dateTo);
                if (userId) qs.set("sent_by", userId);
                if (status) qs.set("status", status);
                if (q) qs.set("q", q);
                if (limit) qs.set("limit", String(limit));
                if (offset) qs.set("offset", String(offset));
                const url = `/wa/accounts/${accId}/reports/search?` + qs.toString();
                const r = await tryGet(url);
                return r?.data || { rows: [], counts: { total: 0, sent: 0, pending: 0, error: 0 } };
            },
        };
    }, []);

    // Carga inicial de canales
    useEffect(() => {
        (async () => {
            const accs = await ep.listAccounts();
            setAccounts(accs);
            if (accs.length) setSelectedAccId(String(accs[0].id));
        })();
    }, [ep]);

    // limpiar error de lote al elegir
    useEffect(() => { setBatchError(""); }, [batchId]);

    // Validación fechas
    useEffect(() => {
        if (!dateFrom && !dateTo) { setDateError("Debes ingresar al menos una fecha (Desde o Hasta)."); return; }
        if (dateFrom && dateTo && dateTo < dateFrom) { setDateError("La fecha 'Hasta' no puede ser menor que 'Desde'."); return; }
        setDateError("");
    }, [dateFrom, dateTo]);

    // Consulta
    const runQuery = async ({ append = false } = {}) => {
        if (!selectedAccId) return;
        setLoading(true);
        try {
            let data;
            const st = status === "all" ? "" : status;

            if (batchId) {
                data = await ep.listBatchJobs(selectedAccId, batchId, {
                    status: st, q, limit: pageSize, offset: append ? offset : 0, dateFrom, dateTo, userId: userFilter
                });
            } else {
                data = await ep.listByUserAndDate(selectedAccId, {
                    status: st, q, limit: pageSize, offset: append ? offset : 0, dateFrom, dateTo, userId: userFilter
                });
            }

            const nextRows = Array.isArray(data.rows) ? data.rows : [];
            const nextCounts = data.counts || { total: 0, sent: 0, pending: 0, error: 0 };

            setCounts(nextCounts);
            if (append) setRows(old => [...old, ...nextRows]); else setRows(nextRows);

            const newOffset = (append ? offset : 0) + nextRows.length;
            setOffset(newOffset);
            setHasMore(nextRows.length === pageSize);
        } finally {
            setLoading(false);
        }
    };

    const search = async () => {
        if (!batchId) { setBatchError("Debes seleccionar un lote específico."); return; }
        if (dateError) return;
        setOffset(0);
        setHasMore(false);
        await runQuery({ append: false });
    };

    const loadMore = async () => {
        if (loading || !hasMore) return;
        await runQuery({ append: true });
    };

    const exportCSV = () => {
        const cols = [
            { label: "Canal", accessor: () => selectedAccId },
            { label: "Lote", accessor: (r) => r.batch_id ?? batchId ?? "" },
            { label: "ID Job", accessor: (r) => r.id ?? "" },
            { label: "Usuario (sent_by)", accessor: (r) => r.sent_by ?? r.user_id ?? "" },
            { label: "Teléfono", accessor: (r) => r.phone ?? "" },
            { label: "Status", accessor: (r) => r.status ?? "" },
            { label: "WA Status", accessor: (r) => r.wa_status ?? "" },
            { label: "Error", accessor: (r) => r.error_msg ?? "" },
            { label: "Enviado en", accessor: (r) => r.sent_at ?? "" },
            { label: "Entregado en", accessor: (r) => r.delivered_at ?? "" },
            { label: "Leído en", accessor: (r) => r.read_at ?? "" },
            { label: "Mensaje preview", accessor: (r) => r.message_preview ?? "" },
            {
                label: "Payload / data", accessor: (r) => {
                    const d = r.data_json || r.meta || r.payload || r.data || null;
                    try { return d && typeof d === "object" ? JSON.stringify(d) : (d ?? ""); } catch { return ""; }
                }
            },
        ];
        const csv = toCSV(rows, cols);
        const fname = `reporte_${selectedAccId}_${batchId || "general"}_${new Date().toISOString().slice(0, 10)}.csv`;
        download(fname, csv);
    };

    const columns = [
        { key: "id", label: "ID", render: r => r.id ?? "" },
        { key: "phone", label: "Teléfono", render: r => r.phone ?? "" },
        { key: "status", label: "Estado", render: r => r.status ?? "" },
        { key: "wa_status", label: "WA", render: r => r.wa_status ?? "" },
        { key: "error", label: "Error", render: r => r.error_msg ?? "" },
        { key: "sent_at", label: "Enviado", render: r => r.sent_at ?? "" },
        { key: "delivered_at", label: "Entregado", render: r => r.delivered_at ?? "" },
        { key: "read_at", label: "Leído", render: r => r.read_at ?? "" },
        {
            key: "preview", label: "Mensaje", render: r => (
                <button className="btn btn-xs" onClick={() => setPreview({ title: `Mensaje #${r.id}`, content: r.message_preview || "" })}>
                    Ver
                </button>
            )
        },
        {
            key: "data", label: "Data", render: r => {
                const d = r.data_json || r.meta || r.payload || r.data || null;
                const text = (() => { try { return d && typeof d === "object" ? JSON.stringify(d) : (d ?? ""); } catch { return ""; } })();
                return text ? <button className="btn btn-xs" onClick={() => setPreview({ title: `Data #${r.id}`, content: text })}>Ver</button> : "—";
            }
        },
    ];

    return (
        <>
            <div className="page-title">Reportes</div>

            {/* Filtros */}
            <section className="card section">
                <div className="row row-3">
                    <label>Canal
                        <select
                            className="input"
                            value={selectedAccId}
                            onChange={e => { setSelectedAccId(e.target.value); setBatchId(""); setRows([]); setCounts({ total: 0, sent: 0, pending: 0, error: 0 }); }}
                        >
                            {(accounts || []).map(a => (
                                <option key={a.id} value={a.id}>{a.label} · {a.phone}</option>
                            ))}
                        </select>
                    </label>

                    {/* ⬇️ Combo de lotes asíncrono */}
                    <label style={{ width: "100%" }}>
                        <BatchCombo
                            accountId={selectedAccId}
                            value={batchId}
                            onChange={(id) => setBatchId(id)}
                            mine={false}
                            defaultStatus=""
                        />
                        {batchError && (
                            <div className="muted" style={{ color: "#b00020", marginTop: 6 }}>
                                {batchError}
                            </div>
                        )}
                    </label>
                </div>

                <div className="row row-4" style={{ marginTop: 10 }}>
                    <label>Usuario (id)
                        <input className="input" placeholder={`Ej: ${user?.id || "123"}`} value={userFilter} onChange={e => setUserFilter(e.target.value)} />
                    </label>
                    <label>Estado
                        <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="pending">Pendiente</option>
                            <option value="sent">Enviado</option>
                            <option value="error">Error</option>
                            <option value="delivered">Entregado</option>
                            <option value="read">Leído</option>
                        </select>
                    </label>
                    <label>Desde
                        <input className="input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    </label>
                    <label>Hasta
                        <input className="input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    </label>
                </div>

                {dateError && (
                    <div className="muted" style={{ color: "#b00020", marginTop: 6 }}>
                        {dateError}
                    </div>
                )}

                <div className="row row-3" style={{ marginTop: 10 }}>
                    <label>Búsqueda
                        <input
                            className="input"
                            placeholder="Teléfono, nombre, campo de data..."
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter" && !disableSearch) search(); }}
                        />
                    </label>
                    <label>Página (tamaño)
                        <input className="input" type="number" min={50} max={2000} value={pageSize}
                            onChange={e => setPageSize(Number(e.target.value || 200))} />
                    </label>

                    <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
                        <button className="btn" onClick={search} disabled={disableSearch}>
                            {loading ? "Buscando..." : "Buscar"}
                        </button>
                        <button
                            className="btn btn-muted"
                            onClick={() => {
                                setBatchId("");
                                setUserFilter("");
                                setStatus("all");
                                setQ("");
                                setDateFrom(today);
                                setDateTo(today);
                                setRows([]);
                                setCounts({ total: 0, sent: 0, pending: 0, error: 0 });
                            }}
                        >
                            Limpiar
                        </button>
                        <button className="btn" onClick={exportCSV} disabled={rows.length === 0}>Exportar CSV</button>
                    </div>
                </div>
            </section>

            {/* KPIs */}
            <section className="card section">
                <div className="muted">
                    Total: <b>{counts.total}</b> · Enviados: <b>{counts.sent}</b> · Pendientes: <b>{counts.pending}</b> · Errores: <b>{counts.error}</b>
                </div>
            </section>

            {/* Tabla */}
            <section className="card section">
                <div style={{ overflowX: "auto" }}>
                    <table>
                        <thead>
                            <tr>
                                {columns.map(c => <th key={c.key}>{c.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={columns.length}><div className="muted">Sin resultados.</div></td></tr>
                            ) : rows.map(r => (
                                <tr key={`${r.id}-${r.phone}-${r.sent_at || ""}`}>
                                    {columns.map(c => <td key={c.key}>{c.render(r)}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn" disabled={loading || !hasMore} onClick={loadMore}>
                        {loading ? "Cargando..." : hasMore ? "Cargar más" : "No hay más"}
                    </button>
                    <div className="muted">Mostrando: <b>{rows.length}</b></div>
                </div>
            </section>

            {/* Modal preview */}
            {preview && (
                <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000
                }}>
                    <div className="card section" style={{ maxWidth: 800, width: "100%", maxHeight: "80vh", overflow: "auto" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                            <h3 style={{ margin: 0 }}>{preview.title}</h3>
                            <button className="btn btn-muted" onClick={() => setPreview(null)}>Cerrar</button>
                        </div>
                        <pre style={{ whiteSpace: "pre-wrap", marginTop: 10 }}>{preview.content || "—"}</pre>
                    </div>
                </div>
            )}
        </>
    );
}
